import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Biography.css';

/*
 * ==============================================
 * HOW TO EDIT (via Claude chat):
 * ==============================================
 * "Update my biography intro paragraph"
 * "Add a new chapter called 'College Years'"
 * "Change the early life text to ..."
 */

const chapters = [
  {
    key: 'roots',
    title: 'Family Roots',
    period: 'A Legacy Spanning Generations',
    content: [
      'The Genomal family story begins over a century ago, when a grand uncle moved to the Philippines in 1889 to take advantage of trading opportunities. Four generations of our family trace their success to that bold decision.',
      'Our roots connect Mumbai and the Sindh province of Pakistan to Manila, creating a unique cross-cultural heritage. Despite our foreign-sounding names, we are proud Philippine citizens with deep ties to both the Philippines and India.',
      'My brothers Nari, Sunder, and I grew up immersed in the values of hard work, family unity, and giving back — principles passed down through generations that would come to define everything we built together.',
    ],
    image: null,
  },
  {
    key: 'education',
    title: 'Education',
    period: 'Hofstra University, New York',
    content: [
      'I pursued my MBA at Hofstra University in New York, where I was exposed to American business thinking and the global marketplace. Those years in New York were formative — the energy of the city, the diversity of perspectives, and the rigorous academic environment all shaped my approach to business and life.',
      'It was during my time in the United States that I began to see the possibilities of bridging markets between the East and West — an insight that would later become the foundation of our family\'s greatest business ventures.',
    ],
    image: null,
  },
  {
    key: 'page-industries',
    title: 'Page Industries',
    period: '1994 — Building a Billion-Dollar Legacy',
    content: [
      'In 1994, my brother Sunder and I co-founded Page Industries with a singular vision: to bring Jockey International to India. Our father had started the family\'s first factory in Manila in 1959, and we saw an extraordinary opportunity to expand the family\'s expertise in apparel to the vast Indian market.',
      'Page Industries became the exclusive licensee and distributor of Jockey products in India, and later Speedo as well. What began as a bold bet on the Indian consumer grew into one of the country\'s most trusted and recognized apparel brands — a billion-dollar enterprise headquartered in Bangalore.',
      'The Genomal family\'s business acumen has been recognized globally. My brothers and I have each been estimated at $1 billion in individual wealth, ranking among the richest Filipinos — a testament to the power of family, vision, and perseverance.',
    ],
    image: null,
  },
  {
    key: 'chummy-chum',
    title: 'Chummy Chum',
    period: '2007 — A Charity of Love',
    content: [
      'In September 2007, I created the Chummy Chum Foundation Philippines, Inc. — a self-funded family initiative unlike any other charity. We call it the "Charity of Love": unconditional love that comes from the heart.',
      'I designed the Chummy Chum character myself — an adorable purple baby dinosaur, over 6 feet tall. His purple color represents the highest level of spiritual awareness. His red and white striped t-shirt is a cheerful manifestation of his happy character. His apple-green toddler shorts represent a child\'s innocence and uncorrupted thoughts.',
      'Unlike other cartoon characters created for fun, Chummy Chum has directed his efforts towards God\'s special little ones — children who are unwell, terminally ill, abused, neglected, orphaned, or from poor communities. He visits hospitals, orphanages, and children\'s centers several days a week, month after month, year after year.',
      'The foundation runs the Smile For Me Little One Program (SFMLOP) 2-3 times weekly. We\'ve distributed over a million coloring books, created a storybook, composed the "Jump with Chummy Chum" song, and expanded to India in 2019. We also run the Child Medical Assistance Program, the Incubator Loan Program, and the Ventilator Loan Program — lending life-saving equipment to public hospitals free of charge.',
      'The foundation is entirely self-funded by our family. We do not solicit cash donations. My wife and our three sons are all personally involved, and together we\'ve made a promise: Chummy Chum will live forever and continue to do his job for generations to come.',
    ],
    image: null,
  },
  {
    key: 'today',
    title: 'Where I Am Today',
    period: 'Present Day',
    content: [
      'Today I serve as Non-Executive Director of Page Industries Limited and continue as Founder, Director, and President of the Chummy Chum Foundation — operating across the Philippines and India from bases in Manila and Bangalore.',
      'My life is a balance of business and compassion. While Page Industries continues to thrive as one of India\'s premier apparel companies, my heart remains with Chummy Chum and the children we serve. The Sheraton Palawan project — a 5-star luxury hotel on a 16-hectare development zone — represents the next chapter of our family\'s vision for the Philippines.',
      'After more than 18 years of operations, the Chummy Chum Foundation has touched countless lives. But for me, the true measure of success has never been in numbers — it\'s in the smiles of children who feel loved, even if just for an afternoon.',
    ],
    image: null,
  },
];

function Biography() {
  const [activeChapter, setActiveChapter] = useState('early-life');
  const chapter = chapters.find((c) => c.key === activeChapter);

  return (
    <section className="biography section-padding" id="biography">
      <div className="container">
        <motion.div
          className="biography__header"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8 }}
        >
          <span className="section-label">My Story</span>
          <h2 className="section-title">
            The story of<br />
            <span className="text-gradient">my life</span>
          </h2>
        </motion.div>

        <div className="biography__layout">
          {/* Chapter Navigation */}
          <motion.div
            className="biography__nav"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {chapters.map((ch, i) => (
              <button
                key={ch.key}
                className={`biography__nav-item ${activeChapter === ch.key ? 'biography__nav-item--active' : ''}`}
                onClick={() => setActiveChapter(ch.key)}
              >
                <span className="biography__nav-number">0{i + 1}</span>
                <div>
                  <span className="biography__nav-title">{ch.title}</span>
                  <span className="biography__nav-period">{ch.period}</span>
                </div>
              </button>
            ))}
          </motion.div>

          {/* Chapter Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeChapter}
              className="biography__content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              {chapter.image && (
                <div className="biography__image">
                  <img src={chapter.image} alt={chapter.title} />
                </div>
              )}
              <h3 className="biography__chapter-title">{chapter.title}</h3>
              <span className="biography__chapter-period">{chapter.period}</span>
              <div className="biography__text">
                {chapter.content.map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

export default Biography;
